import { Injectable, inject, signal, WritableSignal } from "@angular/core";
import { Meta } from "@angular/platform-browser";
import { FuseConfigService } from "@fuse/services/config";
import { GeneralService } from "./general.service";

export type Scheme = "dark" | "light" | "system";

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  public currentTheme: WritableSignal<Scheme> = signal<Scheme>("system");
  // Effective theme actually applied to the UI (resolves 'system' to 'light'|'dark')
  public effectiveTheme: WritableSignal<"light" | "dark"> = signal<
    "light" | "dark"
  >("light");

  private readonly generalService = inject(GeneralService);
  private readonly _fuseConfigService = inject(FuseConfigService);
  private readonly metaService = inject(Meta);

  private readonly lightMetaThemeColor = "#ffffff";
  private readonly darkMetaThemeColor = "#0a0a0a";

  constructor() {
    this.initializeTheme();
    this.setupSystemPreferenceListener();
  }

  private initializeTheme(): void {
    const userTheme = localStorage.getItem("theme") as Scheme;

    if (userTheme && ["dark", "light", "system"].includes(userTheme)) {
      this.currentTheme.set(userTheme);
      this.applyTheme(userTheme);
    } else {
      this.currentTheme.set("system");
      this.applyTheme("system");
    }
  }

  private setupSystemPreferenceListener(): void {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", this.handleSystemPreferenceChange);
  }

  setTheme(theme: Scheme): void {
    this.currentTheme.set(theme);
    localStorage.setItem("theme", theme);
    this.applyTheme(theme);
  }

  private applyTheme(theme: Scheme): void {
    let actualTheme: "dark" | "light";

    if (theme === "system") {
      const systemPrefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)"
      ).matches;
      actualTheme = systemPrefersDark ? "dark" : "light";
    } else {
      actualTheme = theme;
    }

    this.generalService.theme.set(actualTheme);
    this.effectiveTheme.set(actualTheme);

    let metaColor: string;

    switch (actualTheme) {
      case "dark":
        this._fuseConfigService.config = { scheme: "dark" };
        document.body.classList.remove("light-theme");
        document.body.classList.add("dark-theme");
        metaColor = this.darkMetaThemeColor;
        break;
      case "light":
        this._fuseConfigService.config = { scheme: "light" };
        document.body.classList.remove("dark-theme");
        document.body.classList.add("light-theme");
        metaColor = this.lightMetaThemeColor;
        break;
    }

    this.metaService.updateTag({
      name: "theme-color",
      content: metaColor,
    });
  }

  toggleTheme(): void {
    const currentTheme = this.currentTheme();
    let nextTheme: Scheme;

    if (currentTheme === "light") {
      nextTheme = "dark";
    } else if (currentTheme === "dark") {
      nextTheme = "system";
    } else {
      nextTheme = "light";
    }

    this.setTheme(nextTheme);
  }

  private readonly handleSystemPreferenceChange = (): void => {
    if (this.currentTheme() === "system") {
      this.applyTheme("system");
    }
  };

  destroy(): void {
    window
      .matchMedia("(prefers-color-scheme: dark)")
      .removeEventListener("change", this.handleSystemPreferenceChange);
  }
}
